import { useMemo, useState } from 'react'
import { QuoteDelivery } from './QuoteDelivery'

interface ChargeLine {
  id: number
  name: string
  cost: number
  sell: number
  group: string
}

const money = (value: number) => `$${Math.round(value).toLocaleString('en-US')}`

export function QuoteEconomics({
  route,
  equipment,
  freightBuy,
  freightSell = freightBuy,
  reusedFrom,
}: {
  route: string
  equipment: string
  freightBuy: number
  freightSell?: number
  reusedFrom?: string
}) {
  const [lines, setLines] = useState<ChargeLine[]>(() => [
    { id: 1, name: 'Ocean freight', cost: freightBuy, sell: freightSell, group: 'Freight' },
    { id: 2, name: 'Documentation', cost: 35, sell: 95, group: 'Origin' },
    { id: 3, name: 'Handling', cost: 80, sell: 165, group: 'Origin' },
    { id: 4, name: 'Agency commission', cost: 0, sell: 175, group: 'Commercial' },
  ])
  const [open, setOpen] = useState(true)
  const totals = useMemo(() => {
    const cost = lines.reduce((sum, line) => sum + line.cost, 0)
    const sell = lines.reduce((sum, line) => sum + line.sell, 0)
    const profit = sell - cost
    return { cost, sell, profit, margin: sell > 0 ? (profit / sell) * 100 : 0 }
  }, [lines])

  const update = (id: number, field: 'name' | 'cost' | 'sell', value: string) =>
    setLines((current) =>
      current.map((line) =>
        line.id === id ? { ...line, [field]: field === 'name' ? value : Number(value) || 0 } : line
      )
    )
  const add = () =>
    setLines((current) => [
      ...current,
      { id: Date.now(), name: 'Additional charge', cost: 0, sell: 0, group: 'Additional' },
    ])

  return (
    <section className="qec">
      {reusedFrom && (
        <div className="qec-reused">
          <i>↻</i>
          <div>
            <b>Draft reused from {reusedFrom}</b>
            <span>
              Previous charge structure preserved. Current usable freight rate of{' '}
              {money(freightBuy)} injected and ready to edit.
            </span>
          </div>
          <button type="button">Compare changes</button>
        </div>
      )}
      <header>
        <div>
          <span>Quote economics</span>
          <h3>Build profit, charge by charge.</h3>
          <p>
            {route} · {equipment}
          </p>
        </div>
        <button type="button" onClick={() => setOpen(!open)}>
          {open ? 'Collapse' : 'Edit charges'} {open ? '−' : '+'}
        </button>
      </header>
      <div className="qec-total">
        <div>
          <span>Net buy</span>
          <strong>{money(totals.cost)}</strong>
        </div>
        <div>
          <span>Customer sell</span>
          <strong>{money(totals.sell)}</strong>
        </div>
        <div className="profit">
          <span>Quote profit</span>
          <strong>{money(totals.profit)}</strong>
        </div>
        <div>
          <span>Margin</span>
          <strong>{totals.margin.toFixed(1)}%</strong>
        </div>
      </div>
      {open && (
        <>
          <div className="qec-head">
            <span>Charge</span>
            <span>Net cost</span>
            <span>Sell</span>
            <span>Profit</span>
            <span>Margin</span>
            <span />
          </div>
          <div className="qec-lines">
            {lines.map((line) => {
              const profit = line.sell - line.cost
              const margin = line.sell > 0 ? (profit / line.sell) * 100 : 0
              return (
                <div className="qec-line" key={line.id}>
                  <label>
                    <small>{line.group}</small>
                    <input
                      aria-label={`${line.name} charge name`}
                      value={line.name}
                      onChange={(e) => update(line.id, 'name', e.target.value)}
                    />
                  </label>
                  <label>
                    <small>Net cost</small>
                    <span>$</span>
                    <input
                      aria-label={`${line.name} net cost`}
                      type="number"
                      value={line.cost}
                      onChange={(e) => update(line.id, 'cost', e.target.value)}
                    />
                  </label>
                  <label>
                    <small>Sell</small>
                    <span>$</span>
                    <input
                      aria-label={`${line.name} sell`}
                      type="number"
                      value={line.sell}
                      onChange={(e) => update(line.id, 'sell', e.target.value)}
                    />
                  </label>
                  <strong className={profit > 0 ? 'good' : ''}>{money(profit)}</strong>
                  <b>{margin.toFixed(0)}%</b>
                  <button
                    type="button"
                    aria-label={`Remove ${line.name}`}
                    onClick={() =>
                      setLines((current) => current.filter((item) => item.id !== line.id))
                    }
                  >
                    ×
                  </button>
                </div>
              )
            })}
          </div>
          <footer>
            <button type="button" onClick={add}>
              ＋ Add charge
            </button>
            <p>
              Profit can come from any service line—even when freight is passed through at cost.
            </p>
            <button type="button" className="primary">
              Save quote structure →
            </button>
          </footer>
        </>
      )}
      <QuoteDelivery
        route={route}
        totalSell={totals.sell}
        totalProfit={totals.profit}
        lines={lines}
      />
      <style>{styles}</style>
    </section>
  )
}

const styles = `
.qec{margin-top:12px;border:1px solid var(--border);background:var(--surface-0);border-radius:14px;box-shadow:var(--shadow-card);overflow:hidden}.qec *{box-sizing:border-box}.qec>header{display:flex;justify-content:space-between;align-items:start;padding:16px;border-bottom:1px solid var(--border)}.qec>header span{font-family:var(--mono,ui-monospace,monospace);font-size:9px;letter-spacing:.13em;text-transform:uppercase;color:var(--frd-accent);font-weight:700}.qec>header h3{font-family:var(--font-display,Ranade,Georgia,serif);font-size:19px;margin:3px 0}.qec>header p{font-size:9px;color:var(--text-muted);margin:0}.qec>header button{border:1px solid var(--border);background:var(--surface-0);color:inherit;border-radius:8px;padding:7px 9px;font-size:9px}.qec-total{display:grid;grid-template-columns:repeat(4,1fr);background:var(--surface-1);border-bottom:1px solid var(--border)}.qec-total>div{padding:12px 15px;border-right:1px solid var(--border)}.qec-total>div:last-child{border:0}.qec-total span{display:block;font-size:8px;color:var(--text-muted)}.qec-total strong{display:block;font-family:var(--mono,ui-monospace,monospace);font-size:16px;margin-top:4px}.qec-total .profit strong{color:#047857}.qec-head,.qec-line{display:grid;grid-template-columns:minmax(150px,1.5fr) minmax(85px,.7fr) minmax(85px,.7fr) .65fr .5fr 24px;gap:8px;align-items:center}.qec-head{padding:8px 14px;border-bottom:1px solid var(--border);font-family:var(--mono,ui-monospace,monospace);font-size:7px;text-transform:uppercase;color:var(--text-muted)}.qec-line{padding:8px 14px;border-bottom:1px solid var(--border)}.qec-line label{position:relative}.qec-line label small{display:none}.qec-line label span{position:absolute;left:8px;top:8px;font-size:10px;color:var(--text-muted)}.qec-line input{width:100%;border:1px solid transparent;background:transparent;color:inherit;border-radius:7px;padding:7px;font:inherit;font-size:10px}.qec-line label:not(:first-child) input{padding-left:17px;font-family:var(--mono,ui-monospace,monospace)}.qec-line input:hover,.qec-line input:focus{border-color:var(--border);background:var(--surface-1);outline:none}.qec-line>strong,.qec-line>b{font-family:var(--mono,ui-monospace,monospace);font-size:10px}.qec-line>strong.good{color:#047857}.qec-line>button{border:0;background:none;color:var(--text-muted);font-size:16px}.qec>footer{display:grid;grid-template-columns:auto 1fr auto;gap:14px;align-items:center;padding:12px 14px}.qec>footer button{border:0;background:none;color:var(--frd-accent);font-size:9px;font-weight:700}.qec>footer p{margin:0;color:var(--text-muted);font-size:8.5px}.qec>footer button.primary{background:var(--text-primary);color:var(--surface-0);border-radius:8px;padding:8px 10px}
.qec-reused{display:grid;grid-template-columns:28px minmax(0,1fr) auto;gap:10px;align-items:center;padding:10px 14px;background:#10b98110;border-bottom:1px solid #10b98130}.qec-reused>i{width:26px;height:26px;border-radius:50%;display:grid;place-items:center;background:#10b98120;color:#047857;font-style:normal;font-size:14px}.qec-reused b,.qec-reused span{display:block}.qec-reused b{font-size:9.5px;color:#047857}.qec-reused span{font-size:8px;color:var(--text-muted);margin-top:2px}.qec-reused button{border:1px solid #10b98140;background:transparent;color:#047857;border-radius:7px;padding:6px 8px;font-size:8px;font-weight:700}
@media(max-width:760px){.qec-reused{grid-template-columns:28px 1fr}.qec-reused button{grid-column:2;width:max-content}.qec-total{grid-template-columns:1fr 1fr}.qec-total>div{border-bottom:1px solid var(--border)}.qec-head{display:none}.qec-line{grid-template-columns:1fr 1fr}.qec-line label:first-child{grid-column:1/-1}.qec-line label small{display:block;font-size:7px;color:var(--text-muted);margin:0 0 2px 7px}.qec-line label span{top:20px}.qec-line>strong,.qec-line>b{padding:5px 7px}.qec-line>strong:before{content:'Profit ';color:var(--text-muted);font-family:inherit;font-size:7px}.qec-line>b:before{content:'Margin ';color:var(--text-muted);font-family:inherit;font-size:7px}.qec-line>button{position:absolute;right:20px;margin-top:-48px}.qec>footer{grid-template-columns:1fr}.qec>footer p{order:2}.qec>footer button{text-align:left}.qec>footer button.primary{text-align:center}}
`
